import dynamic from 'next/dynamic';
import styled from '@emotion/styled';
import { Content } from '../../apiService';
import ContentGenres from '../../ContentGenres';
import ContentRatings from '../../ContentRatings';
import { Box, Flex, Text } from '../../styles';
import { sanitize } from '../../utils';
import useConditionalProps from '../../utils/useConditionalProps';
import ContentSummary from '../SingleContent/ContentSummary';
import EpisodeItems from './EpisodeItems';

const ContentBookmark = dynamic(() => import('../../ContentBookmark'), { ssr: false });
const Reaction = dynamic(() => import('../../Reaction'), { ssr: false });

type InfoSectionProps = {
  content: Content;
  activeFileId?: string;
  /** Passed on to the episode grid: no episode of this short can be played. */
  episodesDisabled?: boolean;
  onSelectEpisode: (fileId?: string) => void;
};

/** Everything about the short itself. Laid out by its host — a side panel or a bottom sheet. */
export default function InfoSection({ content, activeFileId, episodesDisabled, onSelectEpisode }: InfoSectionProps) {
  const { title, original_name, summary, categories, short_id: contentId } = content;
  const bookmarked = useConditionalProps((s) => s.bookmarked);
  const reaction = useConditionalProps((s) => s.reaction);
  const fetching = useConditionalProps((s) => s.loading);
  // That store is global and filled a render after `content` lands, so until its id matches
  // this short its flags still describe whatever page the viewer came from.
  const settled = useConditionalProps((s) => s.contentId) === contentId;
  const loading = fetching || !settled;

  const { full: summaryHtml } = sanitize({ raw: summary || '' });

  return (
    <>
      <Meta>
        <Flex align="center" justify="space-between">
          <Text variant="h1">
            {title}
            <ContentBookmark isBookmarked={settled && bookmarked} loading={loading} contentId={contentId} />
          </Text>
          <Reaction type="content" flag={settled ? reaction : 'none'} id={contentId} loading={loading} round />
        </Flex>
        {original_name && (
          <Text variant="body1" as="h2" color="weak" mb={1} mt="-.5em" weight="medium" english>
            {original_name}
          </Text>
        )}
        <Box mb={2}>
          <ContentRatings {...content} />
        </Box>
        <ContentSummary html={summaryHtml} />
        {!!categories?.length && <ContentGenres categories={categories} />}
      </Meta>
      <EpisodeItems content={content} activeFileId={activeFileId} disabled={episodesDisabled} onSelect={onSelectEpisode} />
    </>
  );
}

// Grouped so the desktop panel has exactly two flex children: this at its natural height, and
// the episode grid taking everything left over.
const Meta = styled.div({
  flexShrink: 0,
});
